import type { Operation } from '@apollo/client'
import { onError } from '@apollo/client/link/error'

import { transportFailureCopy } from '@web-app/lib/errors/transportFailure'

// Surfaces failed operations to the user; the errorLink reports them. In the
// chain, so every call site gets it without a catch — the caller's `await`
// still throws, which aborts its success path. Only transport failures arrive
// here; payload `errors { message path }` is data and stays the caller's job.
//
// Every operation toasts, queries included: a refetch awaited by a mutation
// rejects the mutation's promise and carries the query's context, not the
// mutation's. Opt out only when the failure isn't the user's action.
//
// Observe-only: never clear `response.errors` — a stripped result reads as
// success to anyone checking `data?.op?.errors`.

// Apollo context override: `false` silences, a string replaces the copy.
export type ErrorToast = false | string

// Declares the key on Apollo's context so reads and writes are typed;
// DefaultContext is otherwise Record<string, any>.
declare module '@apollo/client' {
  interface DefaultContext {
    errorToast?: ErrorToast
  }
}

// Pure policy. Backend prose is never shown.
export const resolveErrorToast = ({
  operation,
  isOffline,
}: {
  operation: Operation
  isOffline: () => boolean
}): ErrorToast => {
  const override = operation.getContext().errorToast
  if (override !== undefined) return override
  return transportFailureCopy(isOffline())
}

type ErrorToastLinkDeps = {
  toast: (message: string) => void
  isOffline: () => boolean
}

// Factory: the browser wires real deps, tests inject fakes.
export const makeErrorToastLink = ({ toast, isOffline }: ErrorToastLinkDeps) =>
  onError(({ operation, networkError }) => {
    // Cancelled by navigation or a timeout: nothing to tell the user.
    if (networkError?.name === 'AbortError') return
    const message = resolveErrorToast({ operation, isOffline })
    if (message) toast(message)
  })
